import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import type { Subject, SubjectCategory } from '../../types';

const subjectSchema = z.object({
  name: z.string().min(1, 'Subject name is required'),
  code: z.string().min(1, 'Subject code is required'),
  examCode: z.string().optional(),
  category: z.enum(['core', 'elective', 'practical', 'humanities', 'stem', 'arts', 'languages', 'pe']),
  department: z.string().optional(),
  color: z.string().optional(),
});

type SubjectFormData = z.infer<typeof subjectSchema>;

interface SubjectFormProps {
  subject?: Subject;
  onSubmit: (data: Subject) => void;
  onCancel: () => void;
}

const categories: { value: SubjectCategory; label: string }[] = [
  { value: 'core', label: 'Core' },
  { value: 'elective', label: 'Elective' },
  { value: 'practical', label: 'Practical' },
  { value: 'humanities', label: 'Humanities' },
  { value: 'stem', label: 'STEM' },
  { value: 'arts', label: 'Arts' },
  { value: 'languages', label: 'Languages' },
  { value: 'pe', label: 'PE' },
];

export const SubjectForm: React.FC<SubjectFormProps> = ({ subject, onSubmit, onCancel }) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<SubjectFormData>({
    resolver: zodResolver(subjectSchema),
    defaultValues: {
      name: subject?.name || '',
      code: subject?.code || '',
      examCode: subject?.examCode || '',
      category: subject?.category || 'core',
      department: subject?.department || '',
      color: subject?.color || '#1976d2',
    },
  });

  const handleFormSubmit = (data: SubjectFormData) => {
    onSubmit(data as Subject);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Subject Name"
                fullWidth
                error={!!errors.name}
                helperText={errors.name?.message}
                required
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="code"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Subject Code"
                fullWidth
                error={!!errors.code}
                helperText={errors.code?.message}
                required
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="examCode"
            control={control}
            render={({ field }) => (
              <TextField {...field} label="Exam Code" fullWidth helperText="e.g., GCSE 8300, A-Level 9709" />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="category"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.category}>
                <InputLabel>Category *</InputLabel>
                <Select {...field} label="Category *">
                  {categories.map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.category && <FormHelperText>{errors.category.message}</FormHelperText>}
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="department"
            control={control}
            render={({ field }) => <TextField {...field} label="Department" fullWidth />}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="color"
            control={control}
            render={({ field }) => (
              <TextField {...field} label="Color" type="color" fullWidth helperText="For timetable display" />
            )}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="contained">
          {subject ? 'Update' : 'Create'} Subject
        </Button>
      </Box>
    </form>
  );
};
